import { Stack } from "@mui/material";
import { useMemo } from "react";
import { ValueDisplayCard } from "../../components/NumericMetricCard.component";
import {
  useEnigmaAvgErrors,
  useEnigmaAvgHints,
  useEnigmaAvgTime,
  useEnigmaFailureRate,
} from "../../hooks";
import {
  useCityEscapeAvgRating,
  useMatchAvgDurationFromCreationTocompletion,
  useMatchAvgDurationFromFirstToLastEnigma,
  useMatchAvgDurationFromPurchaseToEnd,
  useMatchCompletionRate,
  useMatchCount,
  useMatchOpenRate,
  useMatchPlayRate,
} from "../../hooks/cityEscapeStats.hooks";
import { MatchFilter } from "../../libs/statsFilters";
import { formatSecondsToDDHHMMSS } from "../../libs/utils";

export function CityEscapeOverview({
  cityEscapeId,
  filters,
}: {
  cityEscapeId?: string;
  filters?: MatchFilter;
}) {
  return (
    <Stack direction={"column"} spacing={2}>
      <Stack direction="row" spacing={2}>
        <CityEscapeMatchCountCard
          cityEscapeId={cityEscapeId}
          filters={filters}
        />
        <CityEscapeMatchOpenRateCard
          cityEscapeId={cityEscapeId}
          filters={filters}
        />
        <CityEscapeAvgDurationFromCreationCard
          cityEscapeId={cityEscapeId}
          filters={filters}
        />
        <CityEscapeAvgTimePerEnigmaCard
          cityEscapeId={cityEscapeId}
          filters={filters}
        />
        <CityEscapeAvgHintsCard cityEscapeId={cityEscapeId} filters={filters} />
        <CityEscapeAvgErrorsCard
          cityEscapeId={cityEscapeId}
          filters={filters}
        />
        <CityEscapeFailureRateCard
          cityEscapeId={cityEscapeId}
          filters={filters}
        />
        <CityEscapeAvgRatingCard
          cityEscapeId={cityEscapeId}
          filters={filters}
        />
      </Stack>
    </Stack>
  );
}

export function CityEscapeMatchCountCard({
  cityEscapeId,
  filters,
}: {
  cityEscapeId?: string;
  filters?: MatchFilter;
}) {
  const matchCount = useMatchCount({
    selection: { cityEscapeIds: cityEscapeId },
    filters,
  });

  return (
    <ValueDisplayCard
      title="Partite Create"
      value={matchCount.data ?? 0}
      isLoading={matchCount.isLoading}
      error={matchCount.error}
    />
  );
}

export function CityEscapeMatchPlayRateCard({
  cityEscapeId,
  filters,
}: {
  cityEscapeId?: string;
  filters?: MatchFilter;
}) {
  const playRate = useMatchPlayRate({
    selection: { cityEscapeIds: cityEscapeId },
    filters,
  });

  return (
    <ValueDisplayCard
      title="Partite Giocate"
      value={`${playRate.data?.toFixed(2) ?? 0}%`}
      isLoading={playRate.isLoading}
      error={playRate.error}
    />
  );
}

export function CityEscapeMatchCompletionRateCard({
  cityEscapeId,
  filters,
}: {
  cityEscapeId?: string;
  filters?: MatchFilter;
}) {
  const completionRate = useMatchCompletionRate({
    selection: { cityEscapeIds: cityEscapeId },
    filters,
  });

  return (
    <ValueDisplayCard
      title="Partite Terminate"
      value={`${completionRate.data?.toFixed(2) ?? 0}%`}
      isLoading={completionRate.isLoading}
      error={completionRate.error}
    />
  );
}

export function CityEscapeMatchOpenRateCard({
  cityEscapeId,
  filters,
}: {
  cityEscapeId?: string;
  filters?: MatchFilter;
}) {
  const openRate = useMatchOpenRate({
    selection: { cityEscapeIds: cityEscapeId },
    filters,
  });
  return (
    <ValueDisplayCard
      title="Partite Aperte"
      value={`${openRate.data?.toFixed(2) ?? 0}%`}
      isLoading={openRate.isLoading}
      error={openRate.error}
    />
  );
}
export function CityEscapeFailureRateCard({
  cityEscapeId,
  filters,
}: {
  cityEscapeId?: string;
  filters?: MatchFilter;
}) {
  const openRate = useEnigmaFailureRate(cityEscapeId, undefined, filters);

  return (
    <ValueDisplayCard
      title="Soluzioni chieste"
      value={`${openRate.data?.toFixed(2) ?? 0}%`}
      isLoading={openRate.isLoading}
      error={openRate.error}
    />
  );
}

export function CityEscapeAvgHintsCard({
  cityEscapeId,
  filters,
}: {
  cityEscapeId?: string;
  filters?: MatchFilter;
}) {
  const avgHint = useEnigmaAvgHints(cityEscapeId, undefined, filters);

  return (
    <ValueDisplayCard
      title="Media Ispirazioni"
      value={`${avgHint.data?.toFixed(1) ?? 0}`}
      isLoading={avgHint.isLoading}
      error={avgHint.error}
    />
  );
}

export function CityEscapeAvgErrorsCard({
  cityEscapeId,
  filters,
}: {
  cityEscapeId?: string;
  filters?: MatchFilter;
}) {
  const avgErrors = useEnigmaAvgErrors(cityEscapeId, undefined, filters);

  return (
    <ValueDisplayCard
      title="Media Errori"
      value={`${avgErrors.data?.toFixed(1) ?? 0}`}
      isLoading={avgErrors.isLoading}
      error={avgErrors.error}
    />
  );
}

export function CityEscapeAvgDurationFromPurchaseCard({
  cityEscapeId,
  filters,
}: {
  cityEscapeId?: string;
  filters?: MatchFilter;
}) {
  const avgDuration = useMatchAvgDurationFromPurchaseToEnd({
    selection: { cityEscapeIds: cityEscapeId },
    filters,
  });

  const formattedTime = useMemo(() => {
    return avgDuration.data
      ? formatSecondsToDDHHMMSS(avgDuration.data / 1000)
      : "--";
  }, [avgDuration.data]);

  return (
    <ValueDisplayCard
      title="Tempo medio da acquisto"
      value={formattedTime}
      isLoading={avgDuration.isLoading}
      error={avgDuration.error}
    />
  );
}

export function CityEscapeAvgDurationFromCreationCard({
  cityEscapeId,
  filters,
}: {
  cityEscapeId?: string;
  filters?: MatchFilter;
}) {
  const avgDuration = useMatchAvgDurationFromCreationTocompletion({
    selection: { cityEscapeIds: cityEscapeId },
    filters,
  });

  const formattedTime = useMemo(() => {
    return avgDuration.data
      ? formatSecondsToDDHHMMSS(avgDuration.data / 1000, { first: 3 })
      : "--";
  }, [avgDuration.data]);

  return (
    <ValueDisplayCard
      title="Tempo medio"
      value={formattedTime}
      isLoading={avgDuration.isLoading}
      error={avgDuration.error}
    />
  );
}

export function CityEscapeAvgDurationBetweenEnigmasCard({
  cityEscapeId,
  filters,
}: {
  cityEscapeId?: string;
  filters?: MatchFilter;
}) {
  const avgDuration = useMatchAvgDurationFromFirstToLastEnigma({
    selection: { cityEscapeIds: cityEscapeId },
    filters,
  });

  const formattedTime = useMemo(() => {
    return avgDuration.data
      ? formatSecondsToDDHHMMSS(avgDuration.data / 1000)
      : "--";
  }, [avgDuration.data]);

  return (
    <ValueDisplayCard
      title="Tempo medio tra enigmi estremi"
      value={formattedTime}
      isLoading={avgDuration.isLoading}
      error={avgDuration.error}
    />
  );
}

export function CityEscapeAvgRatingCard({
  cityEscapeId,
  filters,
}: {
  cityEscapeId?: string;
  filters?: MatchFilter;
}) {
  const avgRating = useCityEscapeAvgRating(cityEscapeId, filters);

  return (
    <ValueDisplayCard
      title="Valutazione media"
      value={`${avgRating.data?.toFixed(1) ?? 0}`}
      isLoading={avgRating.isLoading}
      error={avgRating.error}
    />
  );
}

export function CityEscapeAvgTimePerEnigmaCard({
  cityEscapeId,
  filters,
}: {
  cityEscapeId?: string;
  filters?: MatchFilter;
}) {
  const avgTimePerEnigma = useEnigmaAvgTime(cityEscapeId, undefined, filters);

  const formattedTime = useMemo(() => {
    return avgTimePerEnigma.data
      ? formatSecondsToDDHHMMSS(avgTimePerEnigma.data / 1000)
      : "--";
  }, [avgTimePerEnigma.data]);

  return (
    <ValueDisplayCard
      title="Tempo medio enigma"
      value={formattedTime}
      isLoading={avgTimePerEnigma.isLoading}
      error={avgTimePerEnigma.error}
    />
  );
}
